import React, { useEffect, useState, useRef } from 'react';
import InfoBanner from '../components/InfoBanner';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { fetchQdrant, deleteQdrant, addQdrant, updateQdrantStatus, QdrantInstance } from '../store/slices/qdrantSlice';
import { fetchFlavors } from '../store/slices/flavorsSlice';
import axiosInstance from '../utility/axiosInstance';
import { useToast } from '../components/ToastContext';
import { useConfirm } from '../components/ConfirmContext';
import Select, { SingleValue } from 'react-select';
import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import Modal from '../components/Modal';
import ManagedBackupsModal from '../components/ManagedBackupsModal';
import TrialLock from '../components/TrialLock';
import useAutoOpenCreateModal from '../hooks/useAutoOpenCreateModal';
import { FaPlus, FaSyncAlt, FaRegCopy, FaCheck } from 'react-icons/fa';
import '../styles/InstancesPage.scss';
import '../styles/DatabasesPage.scss';
import { isFlavorOptionDisabled, platformImage, toFlavorOptions } from '../utility/flavorOptions';
import { NameField, SecretField, formatSizeOption } from '../components/CreateFormFields';
import { secretOk } from '../utility/fieldRules';

type SelectOption = { value: string; label: string; isDisabled?: boolean };

const ITEMS_PER_PAGE = 10;

const QdrantPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const [backupsFor, setBackupsFor] = useState<any>(null);
    const { instances, status } = useSelector((s: RootState) => s.qdrant);
    const flavors = useSelector((s: RootState) => s.flavors.data);
    const images = useSelector((s: RootState) => s.images.data);
    const vmInstances = useSelector((s: RootState) => s.instances.data);

    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [form, setForm] = useState({ name: '', api_key: '' });
    const [flavorOption, setFlavorOption] = useState<SelectOption | null>(null);
    const [keypairOption, setKeypairOption] = useState<SelectOption | null>(null);
    const [keypairs, setKeypairs] = useState<SelectOption[]>([]);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [networkId, setNetworkId] = useState<string | null>(null);
    const [justCreated, setJustCreated] = useState<QdrantInstance & { api_key: string } | null>(null);
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const pollRefs = useRef<Record<number, NodeJS.Timeout>>({});

    const creationLocked = useAutoOpenCreateModal(setShowCreate);

    useEffect(() => {
        dispatch(fetchQdrant());
        dispatch(fetchFlavors());
    }, [dispatch]);

    useEffect(() => {
        if (!showCreate) return;
        Promise.all([
            axiosInstance.get('/v1/keypairs'),
            axiosInstance.get('/v1/private_networks'),
        ]).then(([kpRes, netRes]) => {
            const kps = (Array.isArray(kpRes.data) ? kpRes.data : [])
                .map((k: any) => ({ value: k.keypair?.name ?? '', label: k.keypair?.name ?? '' }))
                .filter((k: SelectOption) => k.value);
            setKeypairs(kps);
            if (kps.length === 1) setKeypairOption(kps[0]);
            const nets = Array.isArray(netRes.data) ? netRes.data : [];
            if (nets[0]?.id) setNetworkId(nets[0].id);
        }).catch(() => { /* ignore */ });
    }, [showCreate]);

    useEffect(() => {
        instances.filter(i => i.status === 'provisioning').forEach(inst => {
            if (pollRefs.current[inst.id]) return;
            pollRefs.current[inst.id] = setInterval(async () => {
                try {
                    const listRes = await axiosInstance.get('/v1/instances');
                    const all = Array.isArray(listRes.data) ? listRes.data : [];
                    const vm = all.find((i: any) => i.name === `qdrant-${inst.name}` && i.status === 'ACTIVE');
                    if (vm) {
                        const ip = vm.metadata?.floatingIp ?? null;
                        clearInterval(pollRefs.current[inst.id]);
                        delete pollRefs.current[inst.id];
                        await axiosInstance.patch(`/v1/qdrant/${inst.id}`, { status: 'ready', floating_ip: ip });
                        dispatch(updateQdrantStatus({ id: inst.id, status: 'ready', floating_ip: ip }));
                    }
                } catch { /* keep polling */ }
            }, 15000);
        });
        return () => { Object.values(pollRefs.current).forEach(clearInterval); };
    }, [instances, dispatch]);

    useEffect(() => {
        setCurrentPage(0);
    }, [instances.length]);

    const totalInstances = instances.length;
    const totalPages = Math.ceil(totalInstances / ITEMS_PER_PAGE);
    const paginatedInstances = instances.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    // Every number from /v1/flavors; a size the datastore image cannot boot is locked with the reason, from that image's
    // own min_ram/min_disk (/v1/images), as on the instance form. One machine's price.
    const flavorOptions: SelectOption[] = toFlavorOptions(flavors, undefined, { image: platformImage(images, 'datastore') });

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!flavorOption) { showToast('Select a server size', 'error'); return; }
        if (!keypairOption) { showToast('Select an SSH keypair', 'error'); return; }
        if (!networkId) { showToast('No private network found. Create one first.', 'error'); return; }
        if (!form.name.trim()) { showToast('Enter a name', 'error'); return; }

        setCreating(true);
        try {
            const res = await axiosInstance.post('/v1/qdrant', {
                name: form.name.trim(),
                api_key: form.api_key.trim() || undefined,
                flavor_id: flavorOption.value,
                network_id: networkId,
                keypair_name: keypairOption.value,
                security_group_names: ['default'],
            });
            setJustCreated(res.data);
            dispatch(addQdrant(res.data));
            setShowCreate(false);
            setForm({ name: '', api_key: '' });
            setFlavorOption(null);
            showToast('Qdrant provisioning started', 'success');
        } catch (err: any) {
            showToast(err.response?.data?.error ?? 'Failed to create Qdrant', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (inst: QdrantInstance) => {
        const proceed = await confirm({
            title: 'Delete Qdrant',
            message: `Delete "${inst.name}"? This will destroy the server and all vector data.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;
        setDeletingId(inst.id);
        try {
            await dispatch(deleteQdrant(inst.id)).unwrap();
            showToast(`${inst.name} deleted`, 'success');
        } catch (err: any) {
            showToast(err.message ?? 'Failed to delete', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    // Prefer the VM's INTERNAL (private-network) IP - managed Qdrant is reachable only from the same
    // private network, so users connect via the private IP, not the floating IP. Mirrors
    // DatabasesPage.hostFor: stored private_ip → derive the "fixed" addr from the VM → floating_ip.
    const hostFor = (inst: QdrantInstance): string | null => {
        if (inst.private_ip) return inst.private_ip;
        const vm: any = (vmInstances ?? []).find((i: any) => i.id === inst.instance_id || i.name === `qdrant-${inst.name}`);
        const internalIp = vm
            ? (Object.values(vm.addresses ?? {}).flat() as any[]).find(a => a?.["OS-EXT-IPS:type"] === "fixed")?.addr
            : null;
        return internalIp ?? inst.floating_ip ?? null;
    };

    const restUrl = (inst: QdrantInstance) => {
        const host = hostFor(inst);
        return host ? `http://${host}:${inst.port}` : null;
    };

    const handleCopyConnection = (inst: QdrantInstance) => {
        const url = restUrl(inst);
        if (!url) return;
        const doCopy = () => {
            if (navigator.clipboard && window.isSecureContext) {
                return navigator.clipboard.writeText(url);
            }
            const el = document.createElement('textarea');
            el.value = url;
            el.style.position = 'fixed';
            el.style.opacity = '0';
            document.body.appendChild(el);
            el.focus();
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            return Promise.resolve();
        };
        doCopy().then(() => {
            setCopiedId(inst.id);
            showToast('Connection string copied', 'success');
            setTimeout(() => setCopiedId(null), 2000);
        }).catch(() => showToast('Failed to copy', 'error'));
    };

    const isLoading = status === 'loading';

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <p className="page-subtitle">
                    Dedicated Qdrant vector database servers. Store and search high-dimensional embeddings at scale.
                </p>
                <a href="https://docs.redu.cloud/Guides/qdrant" target="_blank" rel="noopener noreferrer"
                   className="docs-link">
                    Documentation →
                </a>
            </div>

            <InfoBanner id="qdrant-private-access">
                <span>Private only: connect from the same private network via private IP and API key.</span>
            </InfoBanner>

            <TrialLock>
                <button className="create-button" onClick={() => setShowCreate(true)} title="New Qdrant">
                    <FaPlus />
                    <span>New Vector DB</span>
                </button>
            </TrialLock>

            {justCreated && (
                <div className="db-credentials-banner">
                    <div className="db-cred-header">
                        <span className="db-cred-title">✓ <strong>{justCreated.name}</strong> is provisioning</span>
                        <button className="db-cred-dismiss" onClick={() => setJustCreated(null)}>✕</button>
                    </div>
                    <p className="db-cred-note">Save these credentials now - the API key won&apos;t be shown again.</p>
                    <div className="db-cred-grid">
                        <div className="db-cred-row"><span>Host</span><code>{hostFor(justCreated) ?? 'Provisioning… check back in 5 min'}</code></div>
                        <div className="db-cred-row"><span>REST port</span><code>6333</code></div>
                        <div className="db-cred-row"><span>gRPC port</span><code>6334</code></div>
                        <div className="db-cred-row"><span>API Key</span><code>{justCreated.api_key}</code></div>
                        {restUrl(justCreated) && (
                            <div className="db-cred-row">
                                <span>REST URL</span>
                                <code className="db-cred-conn"
                                    onClick={() => { navigator.clipboard?.writeText(restUrl(justCreated)!); showToast('Copied', 'success'); }}>
                                    {restUrl(justCreated)} <span className="copy-hint">click to copy</span>
                                </code>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="desktop-view">
                <div className="table-container">
                    <table className="keypairs-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>REST Endpoint</th>
                                <th>gRPC Port</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="loading-state">
                                        <FaSyncAlt className="spin" /> Loading Qdrant instances...
                                    </td>
                                </tr>
                            ) : paginatedInstances.length > 0 ? (
                                paginatedInstances.map(inst => (
                                    <tr key={inst.id}>
                                        <td className="keypair-name">{inst.name}</td>
                                        <td className="db-conn">
                                            {restUrl(inst) ? (
                                                <div className="db-conn-wrapper">
                                                    <code>{restUrl(inst)}</code>
                                                    <button
                                                        className={`db-copy-btn${copiedId === inst.id ? ' copied' : ''}`}
                                                        onClick={() => handleCopyConnection(inst)}
                                                        title="Copy connection string"
                                                    >
                                                        {copiedId === inst.id ? <FaCheck /> : <FaRegCopy />}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="db-provisioning">Provisioning…</span>
                                            )}
                                        </td>
                                        <td>{hostFor(inst) ? <code>{hostFor(inst)}:6334</code> : <span className="db-provisioning">Provisioning…</span>}</td>
                                        <td><span className={`db-status db-status-${deletingId === inst.id ? 'provisioning' : inst.status}`}>{deletingId === inst.id ? 'deleting…' : inst.status}</span></td>
                                        <td>{new Date(inst.created_at).toLocaleDateString()}</td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <Select
                                                options={[{ value: 'backups', label: 'Backups' }, { value: 'delete', label: 'Delete Qdrant' }]}
                                                onChange={opt => { if (opt?.value === 'delete') handleDelete(inst); else if (opt?.value === 'backups') setBackupsFor(inst); }}
                                                placeholder="Actions"
                                                isSearchable={false}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={getActionSelectStyles()}
                                            />
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="empty-state">No Qdrant instances yet</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                    disabled={currentPage === 0}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >← Prev</button>
                                <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                    {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalInstances)} of {totalInstances}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={currentPage >= totalPages - 1}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >Next →</button>
                            </div>
                        )}
                </div>
            </div>

            <div className="mobile-view">
                {isLoading ? (
                    <div className="loading-state">
                        <FaSyncAlt className="spin" />
                        <p>Loading Qdrant instances...</p>
                    </div>
                ) : (
                    <div className="cards-container">
                        {paginatedInstances.length > 0 ? paginatedInstances.map(inst => (
                            <div key={inst.id} className="keypair-card">
                                <div className="card-header">
                                    <h3 className="card-title">{inst.name}</h3>
                                    <div style={{ width: '100%', marginTop: 12 }}>
                                        <Select
                                            options={[{ value: 'backups', label: 'Backups' }, { value: 'delete', label: 'Delete Qdrant' }]}
                                            onChange={opt => { if (opt?.value === 'delete') handleDelete(inst); else if (opt?.value === 'backups') setBackupsFor(inst); }}
                                            placeholder="Actions"
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            styles={getActionSelectStyles()}
                                        />
                                    </div>
                                </div>
                                <div className="card-content">
                                    <div className="fingerprint">
                                        <span className="label">REST URL:</span>
                                        <span className="value db-conn">
                                            {restUrl(inst) ? (
                                                <span className="db-conn-wrapper">
                                                    <span>{restUrl(inst)}</span>
                                                    <button
                                                        className={`db-copy-btn${copiedId === inst.id ? ' copied' : ''}`}
                                                        onClick={() => handleCopyConnection(inst)}
                                                        title="Copy connection string"
                                                    >
                                                        {copiedId === inst.id ? <FaCheck /> : <FaRegCopy />}
                                                    </button>
                                                </span>
                                            ) : 'Provisioning…'}
                                        </span>
                                    </div>
                                    <div className="fingerprint"><span className="label">gRPC:</span><span className="value">{hostFor(inst) ? `${hostFor(inst)}:6334` : '-'}</span></div>
                                    <div className="fingerprint"><span className="label">Status:</span><span className="value"><span className={`db-status db-status-${deletingId === inst.id ? 'provisioning' : inst.status}`}>{deletingId === inst.id ? 'deleting…' : inst.status}</span></span></div>
                                    <div className="fingerprint"><span className="label">Created:</span><span className="value">{new Date(inst.created_at).toLocaleDateString()}</span></div>
                                </div>
                            </div>
                        )) : (
                            <div className="empty-state"><p>No Qdrant instances yet</p></div>
                        )}
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                    disabled={currentPage === 0}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >← Prev</button>
                                <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                    {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalInstances)} of {totalInstances}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={currentPage >= totalPages - 1}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >Next →</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <ManagedBackupsModal
                isOpen={!!backupsFor}
                onClose={() => setBackupsFor(null)}
                apiBase="/v1/qdrant"
                serviceId={backupsFor?.id ?? null}
                serviceName={backupsFor?.name ?? ''}
            />

            <Modal isOpen={showCreate && !creationLocked} onClose={() => setShowCreate(false)} title="New Qdrant Vector DB">
                <form onSubmit={handleCreate} className="create-form">
                    <NameField value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))} placeholder="my-vectors" />
                    <SecretField label="API key" value={form.api_key} onChange={v => setForm(p => ({ ...p, api_key: v }))} />
                    <div className="form-group">
                        <label>Server size <span style={{ color: 'var(--color-error,#e53e3e)' }}>*</span></label>
                        <Select
                            options={flavorOptions} value={flavorOption}
                            isOptionDisabled={isFlavorOptionDisabled as any}
                            onChange={(v: SingleValue<SelectOption>) => setFlavorOption(v)}
                            formatOptionLabel={formatSizeOption}
                            placeholder="Select server size…" styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                        />
                    </div>
                    <div className="form-group">
                        <label>SSH keypair <span style={{ color: 'var(--color-error,#e53e3e)' }}>*</span></label>
                        <Select
                            options={keypairs} value={keypairOption}
                            onChange={(v: SingleValue<SelectOption>) => setKeypairOption(v)}
                            placeholder="Select keypair…" styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                        />
                    </div>
                    <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setShowCreate(false)}>Cancel</button>
                        <button type="submit" className="submit-button" disabled={creating || !flavorOption || !keypairOption || !form.name || !secretOk(form.api_key)}>
                            {creating ? 'Creating…' : 'Create Vector DB'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default QdrantPage;
